import en from './translations/en'

const LS_LOCALE_KEY = 'firmadok-language-preference'
const DEFAULT_LOCALE = 'nb'

// Some languages are more specified versions of ones we support.
// For these, we normalise the language string into one we support.
const languageNormalization = {
  // English
  en: 'en',
  'en-GB': 'en', // English (United Kingdom)
  'en-US': 'en', // English (United States)
  'en-CA': 'en', // English (Canada)
  'en-AU': 'en', // English (Australia)
  'en-NZ': 'en', // English (New Zealand)
  // Norwegian
  nb: 'nb',
  'nb-NO': 'nb', // Norwegian (Bokmål)
  'nn-NO': 'nb', // Norwegian (Nynorsk)
  // Russian
  ru: 'ru',
  'ru-BY': 'ru', // Russian (Belarus)
  'ru-KG': 'ru', // Russian (Kyrgyzstan)
  'ru-KZ': 'ru', // Russian (Kazakhstan)
  'ru-MD': 'ru', // Russian (Moldova)
  'ru-RU': 'ru', // Russian (Russia)
  'ru-UA': 'ru', // Russian (Ukraine)
  // Latvian
  lv: 'lv',
  'lv-LV': 'lv', // Latvian (Latvia)
  // Romanian
  ro: 'ro',
  'ro-RO': 'ro', // Romanian (Romania)
  // Polish
  pl: 'pl',
  'pl-PL': 'pl', // Polish (Poland)
} as const

export type AllowedLocale =
  (typeof languageNormalization)[keyof typeof languageNormalization]

function isLanguageNormalizationKey(
  key: string
): key is keyof typeof languageNormalization {
  return key in languageNormalization
}

function isAllowedLocale(locale: string): locale is AllowedLocale {
  return Object.values(languageNormalization).includes(locale as AllowedLocale)
}

export function setPreferredLanguage(language: string) {
  if (!isAllowedLocale(language)) {
    console.error(
      `Attempted to set unsupported language: ${language}. Defaulting to ${DEFAULT_LOCALE}.`
    )
    language = DEFAULT_LOCALE
  }

  localStorage.setItem(LS_LOCALE_KEY, language)
}

export function getPreferredLanguage(): AllowedLocale {
  const saved = localStorage.getItem(LS_LOCALE_KEY)

  if (!saved) {
    const browserLanguage = window.navigator.language
    if (!isLanguageNormalizationKey(browserLanguage)) return DEFAULT_LOCALE

    return languageNormalization[browserLanguage]
  }

  if (!isLanguageNormalizationKey(saved)) return DEFAULT_LOCALE

  return languageNormalization[saved]
}

export type BaseLanguage = typeof en
